import React, { useState, useMemo } from 'react';
import { Button } from '../';
import { Table, Input, Typography, Card } from 'antd';
import {
    HomeOutlined,
    MenuOutlined,
    WindowsFilled,
    LinuxOutlined,
    RightOutlined,
    DownOutlined,
    DesktopOutlined,
} from '@ant-design/icons';
import { getGiBfromKiB, getMountDisplayInfo, getDiskDisplayInfo } from '../../lib/helpers';
import { SourceBasicInfo } from './SourceBasicInfo';
import { InstalledSoftwareTable } from './InstalledSoftwareTable';
import { ServicesTable } from './ServicesTable';
import './SourceInventoryTable.scss';

const { Search } = Input;
const { Text, Title } = Typography;

/**
 * Source Inventory Table Component
 * Displays detailed inventory information for inspected sources
 * Based on the Backbone SourceInspectionGrid component and prepareInventoryInfo logic
 */
export const SourceInventoryTable = ({ sources = [] }) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [searchText, setSearchText] = useState('');

    // Process source data following Backbone prepareInventoryInfo logic
    const processSourceData = (source) => {
        const attributes = source.attributes || {};
        const os_type = source.os_type;
        const migrate_by_vm = source.collection_type === 'vm';

        // Basic information processing
        const cpus = attributes.cpu ? getProcessorDisplayInfo(attributes.cpu.processors) : 'N/A';
        const memory = attributes.memory ? getGiBfromKiB(attributes.memory.total_kb) + ' GiB RAM' : 'N/A';
        const mounts = attributes.storage ? getMountDisplayInfo(os_type, attributes.storage.mounts) : 'N/A';
        const disks = migrate_by_vm && attributes.storage ? getDiskDisplayInfo(attributes.storage.vm_disks) : [];

        // CPU and disk display values
        let cpu_display = '';
        let disks_display = '';

        if (migrate_by_vm) {
            cpu_display = attributes.cpu ? cpus.length.toString() : '';
            const disks_string_array = disks.map(
                (disk) => `${disk.disk_label}, Size: ${disk.size} GiB, ${disk.disk_provisioning_type}`,
            );
            disks_display = disks_string_array.join('; ');
        } else {
            if (attributes.cpu) {
                const processors = cpus.map((cpu) => cpu.processor);
                cpu_display = processors.join('; ');
            }
            if (attributes.storage) {
                const mounts_string_array = mounts.map(
                    (mount) => `${mount.mount_name} Size: ${mount.size} GiB, Free: ${mount.free} GiB`,
                );
                disks_display = mounts_string_array.join('; ');
            }
        }

        // Get inventory payload using the same logic as Backbone RM.getSourceSystemInfo
        const inventory_payload = migrate_by_vm ? {} : getSourceSystemInfo(os_type, attributes);

        return {
            ...source,
            attribute_state: attributes.state === 'success',
            os_version: migrate_by_vm ? makeNiceName(os_type, true) : attributes.os ? attributes.os.desc : 'N/A',
            cpus: cpus,
            cpu_display: cpu_display,
            memory: memory,
            mounts: mounts,
            disks: disks,
            disks_display: disks_display,
            hostname: attributes.network ? attributes.network.hostname : 'N/A',
            migrate_by_vm: migrate_by_vm,
            vm_name: source.vm_name || '',
            ...inventory_payload,
        };
    };

    const getProcessorDisplayInfo = (processors) => {
        if (!processors || !Array.isArray(processors)) return [];
        return processors.map((proc, index) => ({
            processor: `${index + 1}. ${proc.name || proc.model || 'Unknown Processor'}`,
        }));
    };

    const makeNiceName = (os_type, short = false) => {
        const osNames = {
            windows: 'Microsoft Windows',
            linux: 'Linux',
            unix: 'Unix',
        };
        return osNames[os_type] || os_type || 'Unknown';
    };

    const getSourceSystemInfo = (os_type, attributes) => {
        if (attributes.state !== 'success') {
            return {};
        }

        // Inventory data flags
        const showInstalledPrograms = true;
        const showCommands = false;
        let showServices = true;
        let serviceManager = null;

        if (os_type === 'linux') {
            serviceManager = attributes.os ? attributes.os.internals?.service_manager : null;
            showServices = serviceManager === 'systemd' || serviceManager === 'upstart' || serviceManager === 'init';
        }

        const inventory_payload = {
            showServices: showServices,
            showInstalledPrograms: showInstalledPrograms,
            showCommands: showCommands,
        };

        // Installed programs
        if (showInstalledPrograms) {
            if (attributes.os && attributes.os.installed_programs) {
                inventory_payload.installed_programs = attributes.os.installed_programs;
            } else {
                inventory_payload.showInstalledPrograms = false;
            }
        }

        // Services
        if (showServices) {
            let sourceServices =
                attributes.os && attributes.os.internals ? JSON.parse(attributes.os.internals.services || '[]') : null;

            if (sourceServices) {
                if (os_type === 'linux') {
                    sourceServices = sourceServices.filter((service) => service.name !== 'sftpd');
                }

                const services = sourceServices.map((item) => ({
                    name: item.name,
                    caption: item.caption || item.name,
                    source: {
                        available: true,
                        started: item.started,
                        start_mode: item.start_mode,
                        active_status: item.active_status,
                        load_status: item.load_status,
                        goal_status: item.goal_status,
                    },
                }));

                inventory_payload.services = services;
                inventory_payload.serviceManager = serviceManager;
            } else {
                inventory_payload.showServices = false;
            }
        }

        return inventory_payload;
    };

    // Helper function to get storage icon (like StorageInformation component)
    const getStorageIcon = (mount_name) => {
        if (mount_name?.trim()?.toLowerCase() === 'c' || mount_name?.trim() === '/') {
            return <HomeOutlined className="storage-drive-icon" />;
        }
        return <MenuOutlined className="storage-drive-icon" />;
    };

    // Helper function to get OS icon
    const getOSIcon = (osType) => {
        if (osType === 'windows') {
            return <WindowsFilled className="os-icon" />;
        } else if (osType === 'linux' || osType === 'unix') {
            return <LinuxOutlined className="os-icon" />;
        }
        return <DesktopOutlined className="os-icon" />;
    };

    // Process all sources
    const processedSources = useMemo(() => {
        return sources.map((source) => processSourceData(source));
    }, [sources]);

    // Main table columns for source listing
    const sourceColumns = [
        {
            title: '',
            dataIndex: 'expand',
            key: 'expand',
            width: 50,
            render: (_, record) => {
                const isExpanded = expandedRowKeys.includes(record.key);
                return (
                    <Button
                        type="text"
                        size="small"
                        icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                        onClick={() => {
                            const newExpandedKeys = isExpanded
                                ? expandedRowKeys.filter((key) => key !== record.key)
                                : [...expandedRowKeys, record.key];
                            setExpandedRowKeys(newExpandedKeys);
                        }}
                    />
                );
            },
        },
        {
            title: 'IP Address',
            dataIndex: 'host',
            key: 'host',
            width: 150,
        },
        {
            title: 'Hostname',
            dataIndex: 'hostname',
            key: 'hostname',
            width: 200,
        },
        {
            title: 'Operating System Version',
            dataIndex: 'os_version',
            key: 'os_version',
            width: 250,
        },
        {
            title: 'CPU',
            dataIndex: 'cpu_display',
            key: 'cpu_display',
            width: 200,
        },
        {
            title: 'Memory',
            dataIndex: 'memory',
            key: 'memory',
            width: 150,
        },
        {
            title: 'Disks',
            dataIndex: 'disks_display',
            key: 'disks_display',
            width: 200,
            render: (_, record) => {
                return (
                    <Text className={'disk-column'} ellipsis={{ tooltip: true }}>
                        {record.disks_display}
                    </Text>
                );
            },
        },
        {
            title: 'Software',
            dataIndex: 'installed_programs_display',
            key: 'installed_programs_display',
            width: 200,
            render: (_, record) => {
                if (record.installed_programs) {
                    const programsText = record.installed_programs
                        .map((prg) => `${prg.display_name} ${prg.display_version}`)
                        .join('; ');
                    return (
                        <Text className={'disk-column'} ellipsis={{ tooltip: true }}>
                            {programsText}
                        </Text>
                    );
                }
                return '';
            },
        },
    ];

    // Expandable row content
    const expandedRowRender = (record) => {
        return (
            <div className="source-details">
                <Card className="source-detail-card">
                    <div className="source-header">
                        <div className="source-icon">{getOSIcon(record.os_type)}</div>
                        <div className="source-info">
                            <Title level={4}>{record.hostname || record.host}</Title>
                            <Text type="secondary">{record.host}</Text>
                        </div>
                    </div>

                    {record.attribute_state ? (
                        <div className="inventory-sections">
                            <div className="inventory-section">
                                <Title level={5}>Basic Information</Title>
                                <SourceBasicInfo record={record} getStorageIcon={getStorageIcon} />
                            </div>

                            {!record.migrate_by_vm && record.showInstalledPrograms && (
                                <InstalledSoftwareTable programs={record.installed_programs || []} />
                            )}

                            {!record.migrate_by_vm && record.showServices && (
                                <ServicesTable
                                    services={record.services || []}
                                    osType={record.os_type}
                                    serviceManager={record.serviceManager}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <Text type="secondary">Waiting for inspection to complete</Text>
                        </div>
                    )}
                </Card>
            </div>
        );
    };

    // Filter sources based on search
    const filteredSources = processedSources.filter(
        (source) =>
            searchText === '' ||
            (source.hostname && source.hostname.toLowerCase().includes(searchText.toLowerCase())) ||
            (source.host && source.host.toLowerCase().includes(searchText.toLowerCase())) ||
            (source.os_version && source.os_version.toLowerCase().includes(searchText.toLowerCase())),
    );

    // Add keys for table
    const tableData = filteredSources.map((source) => ({
        ...source,
        key: source.id,
    }));

    return (
        <div className="source-inventory-table">
            <div className="table-controls">
                <Search
                    placeholder="Search by inventory information details"
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="inventory-search"
                />
            </div>

            <Table
                columns={sourceColumns}
                dataSource={tableData}
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                    expandedRowRender,
                    expandIcon: () => null, // We handle expand icon manually
                }}
                pagination={false}
                size="small"
                className="inventory-table"
            />
        </div>
    );
};

export default SourceInventoryTable;
